import React, { useRef, useState, useEffect } from 'react';
import { API_BASE_URL } from '../../helpers/ApiSettings';
import { Editor } from '@tinymce/tinymce-react';

const EditProductSaleModal = ({
  editingProductSale,
  setEditingProductSale,
  onSubmit,
  onImageUpload,
  onImageDelete,
  saleCategories = []
}) => {
  const [previewImages, setPreviewImages] = useState([]);
  const [imagesToDelete, setImagesToDelete] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [newImages, setNewImages] = useState([]);
  const editorRef = useRef(null);

  // Reset states when modal opens/closes
  useEffect(() => {
    if (editingProductSale) {
      setPreviewImages([]);
      setImagesToDelete([]);
      setValidationErrors({});
      setNewImages([]);
    }
  }, [editingProductSale]);

  // Validate that at least one image will remain
  const validateImages = () => {
    const existingImages = editingProductSale?.images || [];
    const remainingExistingImages = existingImages.filter(img => !imagesToDelete.includes(img.id));
    const totalImages = remainingExistingImages.length + newImages.length;
    
    if (totalImages === 0) {
      setValidationErrors(prev => ({ ...prev, images: 'Au moins une image est requise' }));
      return false;
    }
    
    setValidationErrors(prev => ({ ...prev, images: null }));
    return true;
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    
    // Create preview URLs for selected images
    const previews = files.map(file => ({
      url: URL.createObjectURL(file),
      file: file
    }));
    
    setPreviewImages([...previewImages, ...previews]);
    setNewImages([...newImages, ...files]);

    // Clear image validation error when adding new images
    setValidationErrors(prev => ({ ...prev, images: null }));
  };

  const handleImageDelete = (imageId, index) => {
    if (imageId) {
      // For existing images, check if we can delete
      const existingImages = editingProductSale?.images || [];
      const remainingExistingImages = existingImages.filter(img => !imagesToDelete.includes(img.id) && img.id !== imageId);
      const totalAfterDeletion = remainingExistingImages.length + newImages.length;
      
      if (totalAfterDeletion === 0) {
        alert('Vous devez garder au moins une image pour le produit.');
        return;
      }

      if (window.confirm('Êtes-vous sûr de vouloir supprimer cette image ?')) {
        setImagesToDelete(prev => [...prev, imageId]);
      }
    } else {
      // For preview images, check if we can delete
      const existingImages = editingProductSale?.images || [];
      const remainingExistingImages = existingImages.filter(img => !imagesToDelete.includes(img.id));
      const totalAfterDeletion = remainingExistingImages.length + newImages.length - 1;
      
      if (totalAfterDeletion === 0) {
        alert('Vous devez garder au moins une image pour le produit.');
        return;
      }

      if (window.confirm('Êtes-vous sûr de vouloir supprimer cette image ?')) {
        // Remove from preview images
        const updatedPreviewImages = previewImages.filter((_, i) => i !== index);
        const updatedNewImages = newImages.filter((_, i) => i !== index);
        
        setPreviewImages(updatedPreviewImages);
        setNewImages(updatedNewImages);
        
        // Cleanup preview URL
        URL.revokeObjectURL(previewImages[index].url);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateImages()) {
      return;
    }

    try {
      // Handle image deletions first
      if (imagesToDelete.length > 0) {
        for (const imageId of imagesToDelete) {
          await onImageDelete(editingProductSale.id, imageId);
        }
      }

      // Upload new images if any
      if (newImages.length > 0) {
        await onImageUpload(editingProductSale.id, newImages);
      }

      // Update the editing product sale with the correct images
      const updatedImages = editingProductSale.images.filter(img => !imagesToDelete.includes(img.id));
      const updatedProductSale = {
        ...editingProductSale,
        images: updatedImages
      };
      
      setEditingProductSale(updatedProductSale);

      // Then submit the form data
      await onSubmit(e);
      
      // Clean up after successful submission
      handleCancel();
      
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Erreur lors de la mise à jour du produit');
    }
  };

  const handleCancel = () => {
    // Clean up preview URLs
    previewImages.forEach(preview => {
      URL.revokeObjectURL(preview.url);
    });
    setPreviewImages([]);
    setImagesToDelete([]);
    setValidationErrors({});
    setNewImages([]);
  };

  // Filter out images marked for deletion
  const visibleExistingImages = editingProductSale?.images?.filter(img => !imagesToDelete.includes(img.id)) || [];

  return (
    <div className="modal" id="editProductSaleModal" tabIndex="-1">
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          {editingProductSale && (
            <form onSubmit={handleSubmit}>
              <div className="modal-header">
                <h5 className="modal-title">Modifier le produit en vente</h5>
                <button type="button" className="btn-close" data-bs-dismiss="modal" onClick={handleCancel}></button>
              </div>
              <div className="modal-body">
                {/* Image Management Section */}
                <div className="mb-4">
                  <label className="form-label">Images du produit <span className="text-danger">*</span></label>
                  {validationErrors.images && (
                    <div className="text-danger small mb-2">{validationErrors.images}</div>
                  )}
                  <div className="d-flex flex-wrap gap-3 mb-3">
                    {/* Existing Images (not marked for deletion) */}
                    {visibleExistingImages.map((image, index) => (
                      <div key={image.id} className="position-relative" style={{ width: '150px', height: '150px' }}>
                        <img
                          src={`${API_BASE_URL}${image.url}`}
                          alt={`Product ${index + 1}`}
                          className="img-thumbnail"
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                        <button
                          type="button"
                          className="btn btn-danger btn-sm position-absolute top-0 end-0"
                          onClick={() => handleImageDelete(image.id)}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                    
                    {/* Preview Images */}
                    {previewImages.map((preview, index) => (
                      <div key={index} className="position-relative" style={{ width: '150px', height: '150px' }}>
                        <img
                          src={preview.url}
                          alt={`Preview ${index + 1}`}
                          className="img-thumbnail"
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                        <button
                          type="button"
                          className="btn btn-danger btn-sm position-absolute top-0 end-0"
                          onClick={() => handleImageDelete(null, index)}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                  
                  <div className="mb-3">
                    <input
                      type="file"
                      className="form-control"
                      multiple
                      accept="image/*"
                      onChange={handleImageChange}
                    />
                    <small className="text-muted">
                      Vous pouvez sélectionner plusieurs images à la fois. Au moins une image est requise.
                    </small>
                  </div>
                </div>

                {/* Product Information */}
                <div className="row">
                  <div className="col-md-8">
                    <div className="mb-3">
                      <label className="form-label">Titre <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        className="form-control"
                        value={editingProductSale.title || ''}
                        onChange={(e) => setEditingProductSale({...editingProductSale, title: e.target.value})}
                        required
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label className="form-label">Code EAN</label>
                      <input
                        type="text"
                        className="form-control"
                        value={editingProductSale.ean_code || ''}
                        onChange={(e) => setEditingProductSale({...editingProductSale, ean_code: e.target.value})}
                        placeholder="Code-barres"
                      />
                    </div>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">Description <span className="text-danger">*</span></label>
                  <Editor
                    onInit={(evt, editor) => editorRef.current = editor}
                    value={editingProductSale.description || ''}
                    init={{
                      height: 300,
                      menubar: false,
                      plugins: [
                        'lists',
                        'autolink',
                        'autoresize',
                        'help',
                        'wordcount'
                      ],
                      toolbar: 'bold italic underline | ' +
                              'bullist numlist | ' +
                              'removeformat help',
                      content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
                      setup: (editor) => {
                        editor.on('init', () => {
                          editor.setContent(editingProductSale.description || '');
                        });
                      }
                    }}
                    onEditorChange={(content) => {
                      setEditingProductSale(prev => ({...prev, description: content}));
                    }}
                  />
                </div>

                {/* Pricing and Stock */}
                <div className="row">
                  <div className="col-md-3">
                    <div className="mb-3">
                      <label className="form-label">Prix unitaire (€) <span className="text-danger">*</span></label>
                      <input
                        type="number"
                        step="0.01"
                        className="form-control"
                        value={editingProductSale.unit_price || ''}
                        onChange={(e) => setEditingProductSale({...editingProductSale, unit_price: parseFloat(e.target.value)})}
                        required
                        min="0"
                      />
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="mb-3">
                      <label className="form-label">Quantité groupée</label>
                      <input
                        type="number"
                        className="form-control"
                        value={editingProductSale.bulk_quantity || 2}
                        onChange={(e) => setEditingProductSale({...editingProductSale, bulk_quantity: parseInt(e.target.value)})}
                        min="2"
                      />
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="mb-3">
                      <label className="form-label">Prix groupé (€)</label>
                      <input
                        type="number"
                        step="0.01"
                        className="form-control"
                        value={editingProductSale.bulk_price || ''}
                        onChange={(e) => setEditingProductSale({...editingProductSale, bulk_price: parseFloat(e.target.value)})}
                        min="0"
                      />
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="mb-3">
                      <label className="form-label">Stock</label>
                      <input
                        type="number"
                        className="form-control"
                        value={editingProductSale.stock_quantity || 0}
                        onChange={(e) => setEditingProductSale({...editingProductSale, stock_quantity: parseInt(e.target.value)})}
                        min="0"
                      />
                    </div>
                  </div>
                </div>

                {/* Product Info */}
                <div className="row">
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label className="form-label">Catégorie</label>
                      <select
                        className="form-select"
                        value={editingProductSale.sale_category?.id ?? editingProductSale.sale_category ?? ''}
                        onChange={(e) => setEditingProductSale({
                          ...editingProductSale,
                          sale_category: e.target.value ? parseInt(e.target.value, 10) : null
                        })}
                      >
                        <option value="">— Aucune —</option>
                        {saleCategories.map((cat) => (
                          <option key={cat.id} value={cat.id}>
                            {cat.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label className="form-label">Marque</label>
                      <input
                        type="text"
                        className="form-control"
                        value={editingProductSale.brand || ''}
                        onChange={(e) => setEditingProductSale({...editingProductSale, brand: e.target.value})}
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label className="form-label">Tranche d'âge</label>
                      <input
                        type="text"
                        className="form-control"
                        value={editingProductSale.age_range || ''}
                        onChange={(e) => setEditingProductSale({...editingProductSale, age_range: e.target.value})}
                        placeholder="ex: 0-6 mois"
                      />
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label className="form-label">Poids (kg)</label>
                      <input
                        type="number"
                        step="0.01"
                        className="form-control"
                        value={editingProductSale.weight || ''}
                        onChange={(e) => setEditingProductSale({...editingProductSale, weight: parseFloat(e.target.value)})}
                        min="0"
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label className="form-label">Statut</label>
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={editingProductSale.is_active}
                          onChange={(e) => setEditingProductSale({...editingProductSale, is_active: e.target.checked})}
                        />
                        <label className="form-check-label">
                          {editingProductSale.is_active ? 'Actif' : 'Inactif'}
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label className="form-label">Produit vedette</label>
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={editingProductSale.featured}
                          onChange={(e) => setEditingProductSale({...editingProductSale, featured: e.target.checked})}
                        />
                        <label className="form-check-label">
                          {editingProductSale.featured ? 'Oui' : 'Non'}
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {editingProductSale.is_private && (
                  <div className="alert alert-info py-2 px-3 small">
                    <strong>Ce produit reste visible par tout le monde.</strong> Les visiteurs anonymes et les nouveaux clients voient le prix unitaire normal.
                    <br />Les clients <strong>déjà référencés</strong> (ayant passé au moins une commande payée) voient en plus le tarif vente privée saisi ci-dessous et paient à ce tarif.
                  </div>
                )}

                <div className="row">
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label className="form-label">Tarif vente privée</label>
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={editingProductSale.is_private ?? false}
                          onChange={(e) => setEditingProductSale({...editingProductSale, is_private: e.target.checked})}
                        />
                        <label className="form-check-label">
                          {editingProductSale.is_private ? 'Activé pour les clients référencés' : 'Désactivé'}
                        </label>
                      </div>
                    </div>
                  </div>
                  {editingProductSale.is_private && (
                    <div className="col-md-4">
                      <div className="mb-3">
                        <label className="form-label">Prix vente privée (€)</label>
                        <input
                          type="number"
                          step="0.01"
                          className="form-control"
                          placeholder="(vide = prix unitaire)"
                          value={editingProductSale.private_price ?? ''}
                          onChange={(e) => setEditingProductSale({
                            ...editingProductSale,
                            private_price: e.target.value === '' ? null : parseFloat(e.target.value)
                          })}
                        />
                        <small className="text-muted">Laisser vide pour appliquer le prix unitaire.</small>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" data-bs-dismiss="modal" onClick={handleCancel}>
                  Annuler
                </button>
                <button type="submit" className="btn btn-primary">
                  Mettre à jour
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditProductSaleModal;